import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import type { AmtFormDefinition, AmtFormSection, AmtFormField } from '../../utils/amtModdleDescriptor';
import {
  FIELD_TYPES,
  createEmptyFormDefinition,
  createEmptyFormSection,
  createEmptyFormField,
} from '../../utils/amtModdleDescriptor';

interface FormDefinitionEditorProps {
  value: AmtFormDefinition | null;
  onChange: (value: AmtFormDefinition) => void;
}

interface FieldEditorProps {
  field: AmtFormField;
  onChange: (field: AmtFormField) => void;
  onDelete: () => void;
}

function FieldEditor({ field, onChange, onDelete }: FieldEditorProps) {
  const [expanded, setExpanded] = useState(false);

  const handleChange = useCallback(
    (key: keyof AmtFormField, value: unknown) => {
      onChange({ ...field, [key]: value });
    },
    [field, onChange]
  );

  const handleLabelChange = useCallback(
    (lang: string, text: string) => {
      onChange({
        ...field,
        labels: { ...field.labels, [lang]: text },
      });
    },
    [field, onChange]
  );

  return (
    <Paper variant="outlined" sx={{ p: 1, mb: 1 }} data-testid="field-editor">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DragIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {field.labels.de || field.id}
            </Typography>
            <Chip
              label={FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
              size="small"
              variant="outlined"
            />
            {field.required && <Chip label="Pflicht" size="small" color="error" />}
          </Box>
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </IconButton>
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {expanded && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              label="Feld-ID"
              value={field.id}
              onChange={(e) => handleChange('id', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Prozessvariable"
              value={field.processVariable}
              onChange={(e) => handleChange('processVariable', e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel>Feldtyp</InputLabel>
            <Select
              value={field.type}
              label="Feldtyp"
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {FIELD_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Bezeichnung (Deutsch)"
            value={field.labels.de || ''}
            onChange={(e) => handleLabelChange('de', e.target.value)}
          />

          <TextField
            size="small"
            label="Platzhalter (Deutsch)"
            value={field.placeholders.de || ''}
            onChange={(e) =>
              onChange({ ...field, placeholders: { ...field.placeholders, de: e.target.value } })
            }
          />

          <TextField
            size="small"
            label="Hinweis (Deutsch)"
            value={field.hints.de || ''}
            onChange={(e) =>
              onChange({ ...field, hints: { ...field.hints, de: e.target.value } })
            }
          />

          <TextField
            size="small"
            label="Standardwert"
            value={field.defaultValue || ''}
            onChange={(e) => handleChange('defaultValue', e.target.value || undefined)}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={field.required}
                  onChange={(e) => handleChange('required', e.target.checked)}
                  size="small"
                />
              }
              label="Pflichtfeld"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={field.readOnly}
                  onChange={(e) => handleChange('readOnly', e.target.checked)}
                  size="small"
                />
              }
              label="Nur lesen"
            />
          </Box>

          {(field.type === 'SELECT' || field.type === 'MULTISELECT') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" fontWeight="medium">
                Optionen
              </Typography>
              {field.options.map((opt, optIndex) => (
                <Box key={optIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Wert"
                    value={opt.value}
                    onChange={(e) => {
                      const newOptions = [...field.options];
                      newOptions[optIndex] = { ...opt, value: e.target.value };
                      handleChange('options', newOptions);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Bezeichnung"
                    value={opt.labels.de || ''}
                    onChange={(e) => {
                      const newOptions = [...field.options];
                      newOptions[optIndex] = {
                        ...opt,
                        labels: { ...opt.labels, de: e.target.value },
                      };
                      handleChange('options', newOptions);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      handleChange(
                        'options',
                        field.options.filter((_, i) => i !== optIndex)
                      );
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleChange('options', [
                    ...field.options,
                    { value: '', labels: { de: '' } },
                  ]);
                }}
              >
                Option hinzufuegen
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

interface SectionEditorProps {
  section: AmtFormSection;
  onChange: (section: AmtFormSection) => void;
  onDelete: () => void;
}

function SectionEditor({ section, onChange, onDelete }: SectionEditorProps) {
  const [expanded, setExpanded] = useState(true);

  const handleTitleChange = useCallback(
    (lang: string, text: string) => {
      onChange({
        ...section,
        titles: { ...section.titles, [lang]: text },
      });
    },
    [section, onChange]
  );

  const handleAddField = useCallback(() => {
    onChange({
      ...section,
      fields: [...section.fields, createEmptyFormField()],
    });
  }, [section, onChange]);

  const handleFieldChange = useCallback(
    (index: number, field: AmtFormField) => {
      const newFields = [...section.fields];
      newFields[index] = field;
      onChange({ ...section, fields: newFields });
    },
    [section, onChange]
  );

  const handleFieldDelete = useCallback(
    (index: number) => {
      onChange({
        ...section,
        fields: section.fields.filter((_, i) => i !== index),
      });
    },
    [section, onChange]
  );

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant="subtitle2">{section.titles.de || section.id}</Typography>
          <Chip label={`${section.fields.length} Felder`} size="small" variant="outlined" />
        </Box>
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{ mr: 1 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              label="Abschnitt-ID"
              value={section.id}
              onChange={(e) => onChange({ ...section, id: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Titel (Deutsch)"
              value={section.titles.de || ''}
              onChange={(e) => handleTitleChange('de', e.target.value)}
              sx={{ flex: 2 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={section.collapsible}
                  onChange={(e) => onChange({ ...section, collapsible: e.target.checked })}
                  size="small"
                />
              }
              label="Einklappbar"
            />
            {section.collapsible && (
              <FormControlLabel
                control={
                  <Switch
                    checked={section.collapsed}
                    onChange={(e) => onChange({ ...section, collapsed: e.target.checked })}
                    size="small"
                  />
                }
                label="Standardmaessig eingeklappt"
              />
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2">Felder</Typography>

          {section.fields.map((field, index) => (
            <FieldEditor
              key={field.id}
              field={field}
              onChange={(f) => handleFieldChange(index, f)}
              onDelete={() => handleFieldDelete(index)}
            />
          ))}

          <Button startIcon={<AddIcon />} onClick={handleAddField} variant="outlined" size="small">
            Feld hinzufuegen
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export function FormDefinitionEditor({ value, onChange }: FormDefinitionEditorProps) {
  const formDefinition = value || createEmptyFormDefinition();
  const [expanded, setExpanded] = useState(true);

  const handleAddSection = useCallback(() => {
    onChange({
      ...formDefinition,
      sections: [...formDefinition.sections, createEmptyFormSection()],
    });
  }, [formDefinition, onChange]);

  const handleSectionChange = useCallback(
    (index: number, section: AmtFormSection) => {
      const newSections = [...formDefinition.sections];
      newSections[index] = section;
      onChange({ ...formDefinition, sections: newSections });
    },
    [formDefinition, onChange]
  );

  const handleSectionDelete = useCallback(
    (index: number) => {
      onChange({
        ...formDefinition,
        sections: formDefinition.sections.filter((_, i) => i !== index),
      });
    },
    [formDefinition, onChange]
  );

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">Formular-Definition</Typography>
          <Chip
            label={`${formDefinition.sections.length} Abschnitte`}
            size="small"
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            label="Formular-ID"
            value={formDefinition.id}
            onChange={(e) => onChange({ ...formDefinition, id: e.target.value })}
          />

          <Typography variant="subtitle2">Abschnitte</Typography>

          {formDefinition.sections.map((section, index) => (
            <SectionEditor
              key={section.id}
              section={section}
              onChange={(s) => handleSectionChange(index, s)}
              onDelete={() => handleSectionDelete(index)}
            />
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddSection}
            variant="outlined"
            size="small"
          >
            Abschnitt hinzufuegen
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
